// client-portal/src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { validateBusinessAddress } from '../utils/validateBusinessAddress';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // SIGNUP FUNCTION - Creates new user
    async function signup(email, password, additionalData = {}) {
        try {
            // Validate BEFORE creating the Firebase Auth account.
            const businessAddress = (additionalData.role || 'client') === 'client'
                ? validateBusinessAddress(additionalData.businessAddress)
                : undefined;
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);

            await setDoc(doc(db, 'users', userCredential.user.uid), {
                uid: userCredential.user.uid,
                email: email,
                role: 'client', // Never trust a self-selected role
                name: additionalData.name || '',
                company: additionalData.company || '',
                phone: additionalData.phone || '',
                ...(businessAddress ? { businessAddress } : {}),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            return userCredential;
        } catch (error) {
            console.error('Signup error:', error);
            throw error;
        }
    }

    function login(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logout() {
        return signOut(auth);
    }

    function resetPassword(email) {
        return sendPasswordResetEmail(auth, email);
    }

    async function loadUserProfile(uid) {
        try {
            const userDocRef = doc(db, 'users', uid);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
                const userData = { uid, ...userDocSnap.data() };
                setUserProfile(userData);
                console.log('User profile loaded:', userData.role);
                return;
            }

            const clientDocRef = doc(db, 'clients', uid);
            const clientDocSnap = await getDoc(clientDocRef);

            if (clientDocSnap.exists()) {
                const clientData = { uid, ...clientDocSnap.data(), role: 'client' };
                setUserProfile(clientData);
                console.log('Client profile loaded');
                return;
            }

            console.warn('No user profile found for UID:', uid);
            setUserProfile(null);

        } catch (error) {
            console.error('Error loading user profile:', error);
            setUserProfile(null);
        }
    }

    // ✅ FIX: Monitor Firebase Auth State (THIS WAS MISSING!)
    useEffect(() => {
        console.log('🔄 Setting up auth listener...');
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            console.log('🔐 Auth state changed:', user ? user.email : 'No user');
            setCurrentUser(user);
            setLoading(false); // ← THIS IS CRITICAL!
        });

        return () => unsubscribe();
    }, []);

    // Load user profile when currentUser changes
    useEffect(() => {
        if (!currentUser) {
            setUserProfile(null);
            return;
        }

        console.log('👤 Loading profile for:', currentUser.uid);

        // Check users collection first
        const userRef = doc(db, 'users', currentUser.uid);
        let unsubContractor = null;
        const unsubUser = onSnapshot(userRef, (docSnap) => {
            if (docSnap.exists()) {
                console.log('✅ Found in users collection');
                setUserProfile(docSnap.data());
            } else {
                // Check contractors collection
                console.log('🔍 Checking contractors collection...');
                const contractorRef = doc(db, 'contractors', currentUser.uid);
                if (unsubContractor) unsubContractor();
                unsubContractor = onSnapshot(contractorRef, (contractorSnap) => {
                    if (contractorSnap.exists()) {
                        console.log('✅ Found in contractors collection');
                        setUserProfile({
                            ...contractorSnap.data(),
                            role: 'contractor'
                        });
                    } else {
                        console.warn('❌ No profile found in users or contractors');
                        setUserProfile(null);
                    }
                });
            }
        });

        return () => { unsubUser(); if (unsubContractor) unsubContractor(); };
    }, [currentUser]);

    const value = {
        currentUser,
        userProfile,
        signup,
        login,
        logout,
        resetPassword,
        loadUserProfile
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}